import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  Self,
  ViewChild,
  ViewChildren
} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {ControlValueAccessor, NgControl} from "@angular/forms";
import {OptionPostition, ValgOptionInput} from "@valg-shared/components/input/input.types";
import {SvgIcon} from "@valg-shared/utils/svg-icon";
import {ActiveDescendantKeyManager} from "@angular/cdk/a11y";
import {InputDropdownOptionComponent} from "./input-dropdown-option/input-dropdown-option.component";
import {lastValueFrom, Subscription } from "rxjs";
import {CdkConnectedOverlay, ConnectedPosition, ScrollStrategy, ScrollStrategyOptions} from "@angular/cdk/overlay";
import {LocalStorageService} from '@valg-shared/services/local-storage-service/local-storage.service';

let NEXT_ID = 0;

@Component({
  selector: "valg-input-dropdown",
  templateUrl: "./input-dropdown.component.html",
})
export class InputDropdownComponent<T> implements ControlValueAccessor, AfterViewInit, DoCheck, OnDestroy
{
  @Input() options: ValgOptionInput<T>[] = [];
  @Input() preSelectFirst = false;
  @Input() dropDownPreSelectKey: string | undefined;
  @Input() includeEmptyOption = true;
  @Input() shouldSortOptions = true;
  @Input() selectedOption?: ValgOptionInput<T>;
  @Input() placeholder: string | undefined;
  @Input() errorKey: string | undefined;
  @Input() dropdownLabel: string | undefined;
  @Input() autoActivateFirstOption: boolean = true;
  @Input() fullWidth: boolean = false;
  @Input() doubleWidth: boolean = false;
  @Input() switchValueGuard?: () => Promise<boolean>;
  @Input() customSortFunction?: (firstOption: ValgOptionInput<T>, secondOption: ValgOptionInput<T>) => number;
  @Output() onBlurEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() onTabAwayEvent: EventEmitter<void> = new EventEmitter<void>();

  @Output() dropdownMenuClose: EventEmitter<void> = new EventEmitter<void>();

  @Output() changeEvent: EventEmitter<ValgOptionInput<T>> = new EventEmitter<
    ValgOptionInput<T>
  >();

  public readonly _emptyOption = { label: 'standard_input.common.empty', isEmptyOption: true, isAlwaysOn: OptionPostition.Top };

  @ViewChild('dropdownTrigger') dropdownTrigger!: ElementRef;
  @ViewChild('otherOptionInput') otherOptionInput!: ElementRef;
  @ViewChild('dropdownMenu') overlay!: ElementRef;

  @ViewChildren(InputDropdownOptionComponent)
  _dropdownOptions!: QueryList<InputDropdownOptionComponent>;

  onChange!: (value?: T | string) => void;
  onTouched!: () => void;

  activeIndex : number | null = null;
  id = `valg-input-dropdown-${NEXT_ID++}`;
  scrollStrategy: ScrollStrategy;

  private belowPosition = {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
    panelClass: 'valg-dropdown-overlay__below',
    offsetY: 16,
  } as ConnectedPosition;

  private abovePosition = {
    originX: 'end',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom',
    panelClass: 'valg-dropdown-overlay__above',
    offsetY: -16,
  } as ConnectedPosition;

  overlayConnectedPositions: ConnectedPosition[] = [
    this.belowPosition,
    this.abovePosition,
  ];

  isMenuVisible = false;
  SvgIcon = SvgIcon;
  displayValue: string | undefined;
  keyManager!: ActiveDescendantKeyManager<InputDropdownOptionComponent>;
  isMenuOverlayTriggered = false;
  @Input() isDisabled = false;
  protected _controlValue?: T;
  private _currentSelectedOption?: ValgOptionInput<T>;
  private _currentOptions?: ValgOptionInput<T>[] = [];
  private _currentPresSelectFirst?: boolean;
  private _currentDropDownPreSelectKey: string | undefined;
  private _hasViewInitialized = false;
  private _maxHeight?: number;

  public setValueOnTab = true;
  public clickedListener!: () => void;
  public selectedItemComponentId: string | undefined;
  private static dropdownMenuOpenEvent = new EventEmitter<string>();

  private $dropdownMenuEventsubscription: Subscription | undefined;

  @ViewChild('optionsContainer') overlayDir!: CdkConnectedOverlay;


  constructor(
    protected readonly _translateService: TranslateService,
    protected readonly _sso: ScrollStrategyOptions,
    public renderer: Renderer2,
    hostElement: ElementRef,
    @Self() public ngControl: NgControl,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly localStorageService: LocalStorageService

  ) {
    this.scrollStrategy = _sso.reposition();
    renderer.addClass(hostElement.nativeElement, "valg-dropdown");
    ngControl.valueAccessor = this;
  }

  ngOnDestroy(): void {
    this.$dropdownMenuEventsubscription?.unsubscribe();
  }

  get formControl() {
    return this.ngControl;
  }

  ngDoCheck(): void {
    if(this.isOptionsChange()){
      if (this.includeEmptyOption) {
        this.addEmptyOption()
      }
    }
    if (!this._hasViewInitialized) {
      return;
    }
    if (
      this.isPreSelectFirstChange() ||
      this.isDropDownPreselectKeyChange() ||
      (this.isSelectedOptionChange() && this.options) ||
      this.isOptionsChange()
    ) {
      if (this.options && this.isOptionsChange() && this.shouldSortOptions) {
        this.sortOptions();
      }
      this.updateValue();
      this._currentPresSelectFirst = this.preSelectFirst;
      this._currentDropDownPreSelectKey = this.dropDownPreSelectKey;
      this._currentOptions = [...this.options ?? []];
      this._currentSelectedOption = this.selectedOption;
    }
  }

  private async addEmptyOption() {
    let placeholder = this.placeholder ? this.placeholder : this._emptyOption.label;
    this._emptyOption.label = await lastValueFrom(this._translateService.get(placeholder));
    if(!this.options.find(option => option.isEmptyOption)) {
      this.options.unshift(this._emptyOption)
    }
  }

  writeValue(obj: T): void {
  this.handleControlValueChange(obj);
  this._controlValue = obj;
  }

  registerOnChange(fn: (value?: T | string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngAfterViewInit() {
    this.keyManager =
      new ActiveDescendantKeyManager<InputDropdownOptionComponent>(
        this._dropdownOptions
      )
        .withTypeAhead();
    this.adjustDropdownSize();
    this._hasViewInitialized = true;

    this.$dropdownMenuEventsubscription = InputDropdownComponent.dropdownMenuOpenEvent.subscribe((id) => {
      if (this.id !== id && this.isMenuVisible) {
        this.closeDropdownMenu(false);
      }
    })
  }

  public focus() {
    this.dropdownTrigger.nativeElement.focus();
  }
  onDropdownClick(event: Event) {
    event.stopPropagation();
    this.isMenuOverlayTriggered = true;
    if (this.isMenuVisible) {
      this.closeDropdownMenu(true, false);
    } else {
      this.onTouched();
      this.openDropdownMenu();
    }
  }

  openDropdownMenu() {
    this.isMenuVisible = true;
    this.isMenuOverlayTriggered = true;
    this.setOutSideClickListener();
    this.cdr.detectChanges();
    if(!this.selectedOption && this.keyManager.activeItem === null){
      if (this.autoActivateFirstOption) {
        this.keyManager.setFirstItemActive();
      }
      this.setActiveIndex();
    }
    InputDropdownComponent.dropdownMenuOpenEvent.emit(this.id);
  }

  setOutSideClickListener(){
    this.removeOutsideClickListener();
    this.clickedListener = this.renderer.listen('document', 'click', (evt) => {
      let target = evt.target as HTMLElement;
      if(!this.overlayDir.overlayRef.overlayElement?.contains(target)){
        this.onBackdropClick();
      }
    });
  }

  removeOutsideClickListener(){
    if(this.clickedListener){
      this.clickedListener();
    }
  }

  closeDropdownMenu(autoFocus = true, selectOnClose = true) {
    this.removeOutsideClickListener();
    if(this.isMenuVisible){
      this.isMenuVisible = false;
      this.isMenuOverlayTriggered = false;
      this.activeIndex = null;
      if(this.keyManager.activeItemIndex !== null && this.keyManager.activeItemIndex >= 0 && selectOnClose){
        let activeOption = this.options[this.keyManager.activeItemIndex]; 
        this.selectItem(activeOption);
      }
      this.dropdownMenuClose.emit();
      if (autoFocus) {
        this.dropdownTrigger?.nativeElement.focus();
      }
      this.onTouched();
    }
  }

  selectAndCloseDropdownMenu() {
    const selectedOption = this.getSelectedOptionFromKeyManager();
    if (selectedOption) {
      this.selectItem(selectedOption, true);
      this.closeDropdownMenu();
    }
  }

  onBackdropClick() {
    this.closeDropdownMenu(false);
    this.isMenuOverlayTriggered = false;
    this.onTouched();
  }

  getDropdownItemComponentId(option?: ValgOptionInput<T>) {
    if (option) {
      return this.id + '-option-' + this.getIndexOfOption(option);
    }
    
    return undefined;
  }

  onSelectionChange(option?: ValgOptionInput<T>) {
    if (option) {
      this.selectedItemComponentId = this.getDropdownItemComponentId(option);
      this.activeIndex = this.getFilteredOptions().indexOf(option);

    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.isDisabled) {
      if (event.code === "Tab") {
        if(this.isMenuVisible){
          if(this.setValueOnTab){
            this.selectAndCloseDropdownMenu();
          } else{
            this.closeDropdownMenu();
          }
        } else {
          this.touchedAndBlur();
          this.onTabAwayEvent.emit();
        }
      } else if (event.code === "Enter") {
        if(this.isMenuVisible){
          this.selectAndCloseDropdownMenu();
        } else{
          this.openDropdownMenu();
        }
      } else if (event.code === "Escape") {
        if(this.isMenuVisible){
          event.stopPropagation();
          this.closeDropdownMenu(true, false);
        }
      } else if (event.code === "ArrowDown") {
        const isActiveIndexNull = this.activeIndex === null;
        this.openDropdownMenu();
        if(isActiveIndexNull){
          this.keyManager.setFirstItemActive();
        } else{
          this.keyManager.setActiveItem(this.activeIndex!);
          this.keyManager.setNextItemActive();
        }
        this.setActiveIndex();
        event.preventDefault();
      } else if (event.code === "ArrowUp") {
        const isActiveIndexNull = this.activeIndex === null;
        this.openDropdownMenu();
        if(isActiveIndexNull){
          this.keyManager.setFirstItemActive();
        } else{
          this.keyManager.setActiveItem(this.activeIndex!);
          this.keyManager.setPreviousItemActive();
        }
        this.setActiveIndex();
        event.preventDefault();
      } else {
        this.openDropdownMenu();
        this.activeIndex = null;
      }
    }
  }

  setActiveIndex(){
    const activeOption = this.getSelectedOptionFromKeyManager();
    if(activeOption){
      this.activeIndex = this.getFilteredOptions().indexOf(activeOption);
    }
  }

  async onItemClicked(event: Event, option: ValgOptionInput<T>) {
    event.stopPropagation();
    if (option.disabled){
      return;
    }
    if (option.value === this.selectedOption?.value){
      this.closeDropdownMenu();
      this.isMenuOverlayTriggered = false;
      return;
    }

    if (this.switchValueGuard !== undefined){
      const canSwitchValue = await this.switchValueGuard();
      if (!canSwitchValue) {
        return;
      }
    }
    this.keyManager.setActiveItem(this.getFilteredOptions().indexOf(option));
    this.selectItem(option, true);
    this.closeDropdownMenu();
    this.isMenuOverlayTriggered = false;
  } 

  selectItem(option: ValgOptionInput<T>, isFromUserAction = false, forceValue = false) {
    if (isFromUserAction) {
      this.onTouched();
      this.onChange(option?.value);

      if (this.dropDownPreSelectKey && option.value && (option.value as any)?.id) 
      {
        this.localStorageService.saveLastSelectedOption(this.dropDownPreSelectKey, (option.value as any).id);
      }
    }
    if(forceValue){
      this.onChange(option?.value);
    }
    this.updateDisplayValueOnChange(option);  
        

  }

  protected updateValue() {
    if (this.selectedOption) {
      this.selectItem(this.selectedOption);
    } else if (this.dropDownPreSelectKey && !this._controlValue) {
        const preSelectedOptionId = this.localStorageService.getLastSelectedOptionFromKey(this.dropDownPreSelectKey);
        const savedOption = this.options.find(option => 
          option.value && (option.value as any)?.id === preSelectedOptionId);
        if (savedOption) {
          this.selectItem(savedOption, false, true);
        } else if (this.preSelectFirst && !this._controlValue) {
          this.selectItem(this.options[0], false, true);
        }
    } else if (this.preSelectFirst && !this._controlValue) {
      this.selectItem(this.options[0], false, true);
    } else if (this._controlValue) {
      this.handleControlValueChange(this._controlValue);
    } else if(!this.selectedOption && !this._controlValue)
    {
      this.keyManager.setActiveItem(-1);
    }
  } 

  protected handleControlValueChange(val: T) {
    const selectedOption = !val ? undefined : this.options?.find((option) => option?.value == val);
    this.updateDisplayValueOnChange(selectedOption);
  }

  protected updateDisplayValueOnChange(option?: ValgOptionInput<T>) {
    if (option) {
      this.activeIndex = this.getFilteredOptions().indexOf(option);
      this.selectedOption = option;
      this.selectedItemComponentId = this.getDropdownItemComponentId(option);
      this.displayValue = option.label;
      this.changeEvent.emit(option);
    } else {
      this.selectedOption = undefined;
      this.selectedItemComponentId = this.getDropdownItemComponentId(option);
      this.displayValue = "";
      this.changeEvent.emit(undefined);
    }
  }

  private sortOptions() {
    if (this.customSortFunction) {
      // Use the custom sort function if provided
      this.options?.sort(this.customSortFunction);
    } else {
      // Default sorting logic
      this.options?.sort((a, b) => {
        if (a.isAlwaysOn !== undefined && a.isAlwaysOn === b.isAlwaysOn) {
          return 0;
        }
        if (a.isAlwaysOn === OptionPostition.Top || b.isAlwaysOn === OptionPostition.Bottom) return -1;
        if (b.isAlwaysOn === OptionPostition.Top || a.isAlwaysOn === OptionPostition.Bottom) return 1;
        return a.label.localeCompare(
            b.label,
            this._translateService.defaultLang,
            { numeric: true, sensitivity: "base" }
        );
      });
    }
  }

  private isPreSelectFirstChange() {
    return this._currentPresSelectFirst !== this.preSelectFirst;
  }

  private isDropDownPreselectKeyChange() {
    return this._currentDropDownPreSelectKey !== this.dropDownPreSelectKey;
  }

  protected isOptionsChange() {
    return this.options?.length !== this._currentOptions?.length;
  }

  private isSelectedOptionChange() {
    return this._currentSelectedOption !== this.selectedOption;
  }

  private getSelectedOptionFromKeyManager() {
    const label = this.keyManager.activeItem?.getLabel() ?? this._currentSelectedOption?.label
    return this.getFilteredOptions().find(
      (option) => label && option.label.includes(label)
    );
  }

  protected getFilteredOptions() {
    return this.options
  }

  private touchedAndBlur() {
    this.onTouched();
    this.onBlurEvent.emit();
  }

  getIndexOfOption(option: ValgOptionInput<T>): number{
    return this.options.indexOf(option);
  }

  getActiveOptions(): ValgOptionInput<T>[]{
    return this.options.filter(x=>!x.disabled);
  }

  get maxHeight(): number | undefined {
    return this._maxHeight;
  }

  @HostListener('window:resize')
  onResize() {
    this.adjustDropdownSize();
  }

  private adjustDropdownSize(): void {
    if (this.overlay === undefined || this.dropdownTrigger === undefined ) {
      return;
    }

    const windowHeight = window.innerHeight;
    const dropdownHeight = this.overlay.nativeElement.offsetHeight;
    const dropdownTop = this.dropdownTrigger.nativeElement.getBoundingClientRect().bottom;
    const maxHeight = windowHeight - dropdownTop - Math.floor(windowHeight * 0.1);
    this._maxHeight = maxHeight < dropdownHeight ? maxHeight : undefined;
  }
}
